import SearchResultsGrid from '@/components/search/SearchResultsGrid';
import SearchResultsHeader from '@/components/search/SearchResultsHeader';
import SearchResultsStatusBanner from '@/components/search/SearchResultsStatusBanner';
import Layout from '@/components/v2/organisms/landing/layout/Layout';
import { memo, useState } from 'react';

import BaseSearchForm from '@/components/forms/BaseSearchForm';
import SimplifiedSearchSummary from '@/components/SimplifiedSearchSummary';
import { useSearchResults } from '@/hooks';
import { useSearchParamsState } from '@/hooks/search/useSearchParamsState';
import { useIsMobile } from '@/hooks/use-mobile';
import GuestsCompareToggle from '@/components/forms/GuestsCompareToggle';

type GuestsCompare = 'maxCapacity' | 'lte';

const SearchResults = memo(() => {
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(false);

  const [guestsCompare, setGuestsCompare] =
    useState<GuestsCompare>('maxCapacity');

  const { checkInDate, checkOutDate, guests } = useSearchParamsState(300);

  const {
    filteredProperties,
    isAnyLoading,
    hasErrors,
    refetchAll,
    guests: guestsRaw,
    checkin: checkinRaw,
    checkout: checkoutRaw,
  } = useSearchResults({
    optimized: false,
    enableAnalytics: true,
    guestsCompare,
  });

  const shouldShowSimplified = isMobile && !isExpanded;

  return (
    <Layout>
      <section className="py-3 bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          {shouldShowSimplified ? (
            <>
              <SimplifiedSearchSummary
                checkIn={checkInDate ?? undefined}
                checkOut={checkOutDate ?? undefined}
                guests={String(guests ?? 1)}
                onClick={() => setIsExpanded(true)}
              />
              <GuestsCompareToggle
                value={guestsCompare}
                onChange={setGuestsCompare}
              />
            </>
          ) : (
            <>
              <BaseSearchForm
                variant={isMobile ? 'mobile' : 'desktop'}
                showTitle={!isMobile}
                initialCheckIn={checkInDate ?? undefined}
                initialCheckOut={checkOutDate ?? undefined}
                initialGuests={String(guests ?? 1)}
                onSubmitted={() => {
                  if (isMobile) setIsExpanded(false);
                }}
              />
              <GuestsCompareToggle
                value={guestsCompare}
                onChange={setGuestsCompare}
              />
            </>
          )}
        </div>
      </section>

      <section className="pt-8 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!isAnyLoading && filteredProperties.length === 0 && (
            <SearchResultsHeader
              filteredPropertiesCount={filteredProperties.length}
              guests={guestsRaw}
              checkin={checkinRaw}
              checkout={checkoutRaw}
            />
          )}

          <SearchResultsStatusBanner
            isAnyLoading={isAnyLoading}
            hasErrors={hasErrors}
            refetchAll={refetchAll}
          />

          <SearchResultsGrid
            filteredProperties={filteredProperties}
            hasErrors={hasErrors}
            refetchAll={refetchAll}
            checkin={checkinRaw}
            checkout={checkoutRaw}
          />
        </div>
      </section>
    </Layout>
  );
});

SearchResults.displayName = 'SearchResults';
export default SearchResults;
